import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import {
  getCurrentMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { mentionLabelsFromDisplayName } from '@/modules/chat-matrix-fast/utils/mentions'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { formatMatrixDisplayName } from '@/modules/chat-matrix-fast/services/matrixUserId'
import type { ChatContact } from '@/modules/chat-matrix-fast/types/ChatContact'

/** Подписи для MXID из текущей Matrix-комнаты (для сопоставления `@Текст` → mxid). */
export function buildMentionLabelByMxid(
  roomId: string | null | undefined,
  mentionUserIds?: string[],
): Map<string, string[]> {
  const map = new Map<string, string[]>()
  if (!roomId) {
    return map
  }

  try {
    const room = getMatrixClient().getRoom(roomId)
    if (!room) {
      return map
    }

    const mxids = mentionUserIds?.length
      ? mentionUserIds
      : room.getJoinedMembers().map(member => member.userId)

    for (const mxid of mxids) {
      if (!mxid?.startsWith('@')) {
        continue
      }

      const member = room.getMember(mxid)
      const rawName = member?.name || member?.rawDisplayName || ''
      const displayName = rawName.startsWith('@') || !rawName
        ? formatMatrixDisplayName(mxid)
        : rawName

      const labels = mentionLabelsFromDisplayName(displayName)
      if (labels.length) {
        map.set(mxid, labels)
      }
    }
  } catch {
    // клиент ещё не готов
  }

  return map
}

export async function openChatForMentionedUser(mxid: string): Promise<void> {
  const normalized = mxid.trim()
  if (!normalized.startsWith('@')) {
    return
  }

  const myMxid = getCurrentMatrixUserId()
  if (myMxid && normalized === myMxid) {
    return
  }

  const coreStore = useChatStoreCore()
  const viewStore = useChatStore()
  const feedback = useFeedbackStore()

  // Уходим из topics-режима группы, иначе сайдбар тем остаётся поверх диалога
  if (
    coreStore.showTopicsList
    || coreStore.activeTopic
    || coreStore.selectedTopicsGroupId != null
  ) {
    coreStore.closeTopicsNavigation()
  }

  const existing = coreStore.chats.find(chat => {
    if (chat.type !== 'dialog' && chat.type !== 'direct') {
      return false
    }
    return chat.matrix_peer_id === normalized
  })

  if (existing) {
    await viewStore.ensureChatListTabForChat(existing)
    await viewStore.openExistingChat(existing, existing.id)
    return
  }

  const employeeId = await coreStore.resolveEmployeeIdByMatrixUserId(normalized)
  if (!employeeId) {
    feedback.showToast({
      type: 'error',
      message: 'Не удалось найти сотрудника для упоминания',
      time: 4000,
    })
    return
  }

  const contact: ChatContact = {
    id: employeeId,
    employeeId,
    fullName: formatMatrixDisplayName(normalized),
    role: '',
    about: '',
    avatar: '',
    online: false,
    matrixUserId: normalized,
  }

  await viewStore.openDirectChatWithEmployee(contact)
}
